import dayjs from 'dayjs';
import { Role, VisitorGroup } from 'graphql/generated/graphqlTypes';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { uniq, uniqBy } from 'lodash';
import { getVisitorOrigin, VisitorOrigin } from 'v2/utils/visitorOrigin';

export const VisitorGroupType = {
  Administrators: 'ADMINISTRATORS',
  AtlasInternal: 'ATLAS_INTERNAL',
  AzureAd: 'AZURE_AD',
  CoContractors: 'CO_CONTRACTORS',
  ExternalVisitors: 'EXTERNAL_VISITORS',
  Installers: 'INSTALLERS'
} as const;

export type VisitorGroupType = (typeof VisitorGroupType)[keyof typeof VisitorGroupType];

interface WithId {
  id: string;
}

/**
 * The parts of a visitor group the access helpers read: the ids of its members, and of its access grant locations
 * (device groups, with the ids of their doors) and doors (devices). Both a full `VisitorGroup` and a group rebuilt
 * from the slim groups query's ids fit it.
 */
export interface VisitorGroupAccessIds {
  id: string;
  members: WithId[];
  accessGrants: { id: string; locations: (WithId & { doors: WithId[] })[]; doors: WithId[] }[];
}

export const getAllAccessGrantsOfVisitorGroup = <T extends Pick<VisitorGroupAccessIds, 'accessGrants'>>(
  visitorGroup: T
): T['accessGrants'] => visitorGroup.accessGrants;

export const getVisitorGroupsThatHaveLockGroupAssigned = <T extends VisitorGroupAccessIds>(
  visitorGroups: T[],
  lockGroupId: string
) =>
  visitorGroups.filter((visitorGroup) =>
    getAllAccessGrantsOfVisitorGroup(visitorGroup).some((accessGrant) =>
      accessGrant.locations.some((location) => location.id === lockGroupId)
    )
  );

export const getVisitorGroupsThatHaveLockAssignedDirectly = <T extends VisitorGroupAccessIds>(
  visitorGroups: T[],
  lockId: string
) =>
  visitorGroups.filter((visitorGroup) =>
    getAllAccessGrantsOfVisitorGroup(visitorGroup).some((accessGrant) =>
      accessGrant.doors.some((door) => door.id === lockId)
    )
  );

export const getVisitorGroupsThatHaveLockAssignedThroughLockGroup = <T extends VisitorGroupAccessIds>(
  visitorGroups: T[],
  lockId: string
) =>
  visitorGroups.filter((visitorGroup) =>
    getAllAccessGrantsOfVisitorGroup(visitorGroup).some((accessGrant) =>
      accessGrant.locations.some((location) => location.doors.some((door) => door.id === lockId))
    )
  );

export const getVisitorGroupsThatHaveLockAssigned = <T extends VisitorGroupAccessIds>(
  visitorGroups: T[],
  lockId: string
) =>
  visitorGroups.filter((visitorGroup) =>
    getAllAccessGrantsOfVisitorGroup(visitorGroup).some(
      (accessGrant) =>
        accessGrant.locations.some((location) => location.doors.some((door) => door.id === lockId)) ||
        accessGrant.doors.some((door) => door.id === lockId)
    )
  );

export const getUniqueMembersList = (visitorGroups: VisitorGroup[]) =>
  uniqBy(
    visitorGroups.flatMap((group) => group.members),
    'id'
  );

export const filterOnlyCoContractorsGroups = <T extends Pick<VisitorGroup, 'type'>>(visitorGroups: T[] | undefined) =>
  visitorGroups?.filter((group) => group.type === VisitorGroupType.CoContractors);

export const filterVisitorGroupsWithoutInternalGroups = <T extends Pick<VisitorGroup, 'type'>>(
  visitorGroups: T[] | undefined
) =>
  visitorGroups?.filter(
    (group) => group.type !== VisitorGroupType.Administrators && group.type !== VisitorGroupType.AtlasInternal
  );

export const filterVisitorGroupsWithMember = <T extends Pick<VisitorGroupAccessIds, 'members'>>(
  visitorGroups: T[] | undefined,
  visitorId: string
) => visitorGroups?.filter((group) => group.members.some((member) => member.id === visitorId));

export const getVisitorGroupOrigin = (visitorGroup: VisitorGroup, translation: Translation) => {
  switch (visitorGroup.type) {
    case VisitorGroupType.Administrators:
    case VisitorGroupType.AtlasInternal:
    case VisitorGroupType.ExternalVisitors:
      return translation.system;
    case VisitorGroupType.CoContractors:
    case VisitorGroupType.Installers:
      return translation.custom;
    case VisitorGroupType.AzureAd:
      return translation.entra_id;
    default:
      return undefined;
  }
};

export const isOfficeModeEnabledForVisitorGroup = (visitorGroup: VisitorGroup) =>
  getAllAccessGrantsOfVisitorGroup(visitorGroup).some((accessGrant) => accessGrant.isOfficeModeEnabled);

export const getTranslatedDayName = (dayName: string, translation: Translation) => {
  switch (dayName.toLowerCase()) {
    case 'monday':
      return translation.monday;
    case 'tuesday':
      return translation.tuesday;
    case 'wednesday':
      return translation.wednesday;
    case 'thursday':
      return translation.thursday;
    case 'friday':
      return translation.friday;
    case 'saturday':
      return translation.saturday;
    case 'sunday':
      return translation.sunday;

    default:
      return '';
  }
};

const getTranslatedDayNameAbbr = (dayName: string, translation: Translation) => {
  switch (dayName.toLowerCase()) {
    case 'monday':
      return translation.mondayAbbr;
    case 'tuesday':
      return translation.tuesdayAbbr;
    case 'wednesday':
      return translation.wednesdayAbbr;
    case 'thursday':
      return translation.thursdayAbbr;
    case 'friday':
      return translation.fridayAbbr;
    case 'saturday':
      return translation.saturdayAbbr;
    case 'sunday':
      return translation.sundayAbbr;

    default:
      return '';
  }
};

export const getScheduleDaysForVisitorGroupGroup = (visitorGroup: VisitorGroup, translation: Translation): string => {
  const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  const days: string[] = [];
  const accessGrants = getAllAccessGrantsOfVisitorGroup(visitorGroup);
  accessGrants.forEach((grant) => {
    grant.accessSchedule.weekDays.forEach((day) => days.push(day.dayName.toLowerCase()));
  });

  const groupedConsecutiveDays = [];
  let start = 0;
  for (let i = 1; i <= days.length; i++) {
    const isConsecutive = i < days.length && dayOrder.indexOf(days[i]) === dayOrder.indexOf(days[i - 1]) + 1;
    if (!isConsecutive) {
      if (i - start > 1) {
        groupedConsecutiveDays.push(
          `${getTranslatedDayNameAbbr(days[start], translation)} - ${getTranslatedDayNameAbbr(days[i - 1], translation)}`
        );
      } else {
        groupedConsecutiveDays.push(getTranslatedDayNameAbbr(days[start], translation));
      }
      start = i;
    }
  }
  return groupedConsecutiveDays.join(', ');
};

const CONSTANT_DATE = '2000-01-01';

const getTimeFormatWithoutSec = (time?: string) =>
  time ? dayjs(`${CONSTANT_DATE} ${time}`).format('HH:mm') : undefined;

export const getScheduleTimeForGroup = (visitorGroup: VisitorGroup): string => {
  const days: Array<string> = [];
  const accessGrants = getAllAccessGrantsOfVisitorGroup(visitorGroup);

  accessGrants.forEach((grant) => {
    grant.accessSchedule.weekDays.forEach((day) =>
      days.push(`${getTimeFormatWithoutSec(day.from)} - ${getTimeFormatWithoutSec(day.to)}`)
    );
  });
  return uniq(days).length > 1 ? 'Mixed' : days[0];
};

export const getAllLockGroupsAssignedToVisitorGroup = (visitorGroup: VisitorGroup) => {
  const accessGrants = getAllAccessGrantsOfVisitorGroup(visitorGroup);
  const allLockGroupsAssignedToVisitorGroup = accessGrants.flatMap((grant) => grant.locations);

  // not sure if it should be unique here or in page code
  return uniqBy(allLockGroupsAssignedToVisitorGroup, 'id');
};

export const getAllLocksAssignedDirectlyToVisitorGroup = (visitorGroup: VisitorGroup) => {
  const accessGrants = getAllAccessGrantsOfVisitorGroup(visitorGroup);
  const allLocksAssignedDirectly = accessGrants.flatMap((grant) => grant.doors);
  return uniqBy(allLocksAssignedDirectly, 'id');
};

export const isAnyMemberAssignedToGroup = (visitorGroup: VisitorGroup) => visitorGroup.members.length > 0;

export const isScheduleSetUpForVisitorGroup = (visitorGroup: VisitorGroup) => {
  const accessGrants = getAllAccessGrantsOfVisitorGroup(visitorGroup);
  return accessGrants.some((grant) => grant.accessSchedule.weekDays.length > 0);
};

export const isAnyLockAssignedToVisitorGroup = (visitorGroup: VisitorGroup) => {
  const accessGrants = getAllAccessGrantsOfVisitorGroup(visitorGroup);

  return accessGrants.some((grant) => grant.doors.length > 0 || grant.locations.length > 0);
};
export const getAccessStatusForVisitorGroup = (visitorGroup: VisitorGroup) =>
  isAnyMemberAssignedToGroup(visitorGroup) &&
  isAnyLockAssignedToVisitorGroup(visitorGroup) &&
  isScheduleSetUpForVisitorGroup(visitorGroup);

export const getAmountOfKeySharingManagers = (visitorGroup: VisitorGroup) => {
  const keySharingManagers = visitorGroup.members.filter((member) =>
    member.visitorGroupMembership.some(
      (membership) =>
        membership.visitorGroup.id === visitorGroup.id && membership.role === Role.ExternalGroupUserManager
    )
  );
  return keySharingManagers.length;
};

export const getAmountOfMembersInGroup = (visitorGroup: VisitorGroup) => visitorGroup.members.length;
export const getMaxNumberOfMembersInGroup = (visitorGroup: VisitorGroup) => visitorGroup.maxNumberOfMembers;

interface SetupStatus {
  isAddUserComplete: boolean;
  isSetScheduleComplete: boolean;
  isAddDeviceComplete: boolean;
}
export const getSetupStatus = (visitorGroup: VisitorGroup): SetupStatus => ({
  isAddDeviceComplete: isAnyLockAssignedToVisitorGroup(visitorGroup),
  isSetScheduleComplete: isScheduleSetUpForVisitorGroup(visitorGroup),
  isAddUserComplete: isAnyMemberAssignedToGroup(visitorGroup)
});

export const getAmountOfCompleteSetupStatuses = (visitorGroup: VisitorGroup) =>
  Object.values(getSetupStatus(visitorGroup)).filter((value) => value).length;

export const getVisitorGroupById = <T extends WithId>(visitorGroups: T[], visitorGroupId: string) =>
  visitorGroups.find((group) => group.id === visitorGroupId);

export const isQRInviteEnabledForVisitorGroup = (visitorGroup: VisitorGroup) =>
  Boolean(visitorGroup.isQrCodeVisitInviterGroup);

export const getKeyManagersAssignedToGroup = (visitorGroup: VisitorGroup) =>
  visitorGroup.members.filter((member) =>
    member.visitorGroupMembership.some(
      (membership) =>
        membership.visitorGroup.id === visitorGroup.id && membership.role === Role.ExternalGroupUserManager
    )
  );

export const getKeySharingInvitedMembers = (visitorGroup: VisitorGroup) =>
  visitorGroup.members.filter(
    (member) =>
      member.visitorGroupMembership.some(
        (membership) => membership.visitorGroup.id === visitorGroup.id && membership.role === Role.ExternalGroupUser
      ) && getVisitorOrigin(member) === VisitorOrigin.KeySharing
  );

// Counts `memberIds` so groups from the slim queries (which don't select `members`) can be checked too.
export const isMaxNumberOfUsersReached = (
  visitorGroup: Pick<VisitorGroup, 'maxNumberOfMembers' | 'memberIds'>
): boolean => {
  return visitorGroup.maxNumberOfMembers != null && visitorGroup.memberIds.length >= visitorGroup.maxNumberOfMembers;
};
